import React from 'react'
import PropTypes from 'prop-types'
import debounce from 'lodash.debounce'

interface PropConstraints<T> {
  readonly value?: string | number
  readonly onChange: React.ChangeEventHandler<T>
}

export type DebounceInputProps<WrappedComponent, WrappedComponentProps> = WrappedComponentProps & {
  readonly element?: string | React.ComponentType<PropConstraints<WrappedComponent>>
  readonly type?: string
  readonly onChange: React.ChangeEventHandler<WrappedComponent>
  readonly onKeyDown?: React.KeyboardEventHandler<WrappedComponent>
  readonly onBlur?: React.FocusEventHandler<WrappedComponent>
  readonly value?: string | number
  readonly placeholder?: string | number
  readonly minLength?: number
  readonly debounceTimeout?: number
  readonly forceNotifyByEnter?: boolean
  readonly forceNotifyOnBlur?: boolean
  readonly inputRef?: React.Ref<WrappedComponent>
}

export type Debounced<
  WrappedComponent,
  WrappedComponentProps
> = React.ComponentType<DebounceInputProps<WrappedComponent, WrappedComponentProps>>;

export type DebounceTextArea = Debounced<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>;

export class XInputDebounce<
  WrappedComponent = HTMLInputElement,
  WrappedComponentProps = React.InputHTMLAttributes<HTMLInputElement>
> extends React.PureComponent<DebounceInputProps<WrappedComponent, WrappedComponentProps>> {

  static propTypes = {
    element: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    type: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onKeyDown: PropTypes.func,
    onBlur: PropTypes.func,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    minLength: PropTypes.number,
    debounceTimeout: PropTypes.number,
    forceNotifyByEnter: PropTypes.bool,
    forceNotifyOnBlur: PropTypes.bool,
    inputRef: PropTypes.func
  }


  static defaultProps = {
    element: 'input',
    type: 'text',
    onKeyDown: undefined,
    onBlur: undefined,
    value: undefined,
    minLength: 0,
    debounceTimeout: 100,
    forceNotifyByEnter: true,
    forceNotifyOnBlur: true,
    inputRef: undefined
  }

  public state: any = {}
  public isDebouncing: boolean = false
  public flush: any = null
  public cancel: any = null
  public notify: any = null

  constructor(props: any) {
    super(props)

    this.isDebouncing = false
    this.state = {value: typeof props.value === 'undefined' || props.value === null ? '' : props.value};

    const {debounceTimeout} = this.props as DebounceInputProps<any, any>
    this.createNotifier(debounceTimeout)
  }


  componentDidUpdate(prevProps: any) {
    if (this.isDebouncing) {
      return;
    }
    const {value, debounceTimeout} = this.props as DebounceInputProps<any, any>

    const {debounceTimeout: oldTimeout, value: oldValue} = prevProps;
    const {value: stateValue} = this.state;

    if (typeof value !== 'undefined' && oldValue !== value && stateValue !== value) {
      // Update state.value if new value passed via props, yep re-render should happen
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({value});
    }
    if (debounceTimeout !== oldTimeout) {
      this.createNotifier(debounceTimeout);
    }
  }


  componentWillUnmount() {
    if (this.flush) {
      this.flush()
    }
  }


  onChange = (event: any) => {
    event.persist();

    const {value: oldValue} = this.state;
    const {minLength} = this.props;

    this.setState({value: event.target.value}, () => {
      const {value} = this.state;

      if (minLength != undefined && value.length >= minLength) {
        this.notify(event);
        return;
      }

      // If user hits backspace and goes below minLength consider it cleaning the value
      if (oldValue.length > value.length) {
        this.notify({...event, target: {...event.target, value: ''}});
      }
    });
  };


  onKeyDown = (event: any) => {
    if (event.key === 'Enter') {
      this.forceNotify(event);
    }
    // Invoke original onKeyDown if present
    const {onKeyDown} = this.props;
    if (onKeyDown) {
      event.persist();
      onKeyDown(event);
    }
  };


  onBlur = (event: any) => {
    this.forceNotify(event);
    // Invoke original onBlur if present
    const {onBlur} = this.props;
    if (onBlur) {
      event.persist();
      onBlur(event);
    }
  };


  createNotifier = (debounceTimeout: any) => {
    if (debounceTimeout < 0) {
      this.notify = () => null
    } else if (debounceTimeout === 0) {
      this.notify = this.doNotify
    } else {
      const debouncedChangeFunc = debounce((event: any) => {
        this.isDebouncing = false
        this.doNotify(event)
      }, debounceTimeout)

      this.notify = (event: any) => {
        this.isDebouncing = true;
        debouncedChangeFunc(event)
      }

      this.flush = () => debouncedChangeFunc.flush()

      this.cancel = () => {
        this.isDebouncing = false;
        debouncedChangeFunc.cancel()
      }
    }
  }


  doNotify = (...args: any[]) => {
    const {onChange} = this.props as DebounceInputProps<any, any>
    onChange(...args)
  }


  forceNotify = (event: any) => {
    const {debounceTimeout} = this.props;
    if (!this.isDebouncing && debounceTimeout != undefined && debounceTimeout > 0) {
      return
    }

    if (this.cancel) {
      this.cancel()
    }

    const {value} = this.state;
    const {minLength} = this.props;

    if (minLength != undefined && value.length >= minLength) {
      this.doNotify(event);
    } else {
      this.doNotify({...event, target: {...event.target, value}});
    }
  };


  render() {
    const {
      element,
      onChange: _onChange,
      value: _value,
      minLength: _minLength,
      debounceTimeout: _debounceTimeout,
      forceNotifyByEnter,
      forceNotifyOnBlur,
      onKeyDown,
      onBlur,
      inputRef,
      ...props
    } = this.props as DebounceInputProps<any, any>
    const { value } = this.state

    let maybeOnKeyDown: any
    if (forceNotifyByEnter) {
      maybeOnKeyDown = {onKeyDown: this.onKeyDown}
    } else if (onKeyDown) {
      maybeOnKeyDown = {onKeyDown}
    } else {
      maybeOnKeyDown = {}
    }

    let maybeOnBlur: any
    if (forceNotifyOnBlur) {
      maybeOnBlur = {onBlur: this.onBlur}
    } else if (onBlur) {
      maybeOnBlur = {onBlur}
    } else {
      maybeOnBlur = {}
    }

    const maybeRef = inputRef ? {ref: inputRef} : {};

    return React.createElement(element, {
      ...props,
      onChange: this.onChange,
      value,
      ...maybeOnKeyDown,
      ...maybeOnBlur,
      ...maybeRef
    })
  }
}